import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MweConstants } from 'src/app/constants/mwe-constants.service';
import { UserDetails } from 'src/app/models/authData';
import { EndCustomerService } from 'src/app/services/end-customer.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { MasterDataService } from 'src/app/services/master-data.service';
import Swal from 'sweetalert2';
import { concatMap, expand, finalize, takeWhile } from 'rxjs';
import { EMPTY } from 'rxjs';

@Component({
  selector: 'app-personal-infomation',
  templateUrl: './personal-infomation.component.html',
  styleUrls: ['./personal-infomation.component.scss']
})
export class PersonalInfomationComponent implements OnInit, OnChanges {
  @Input() personalForm!: FormGroup;
  @Input() showPersonalInfo: boolean = false;
  @Input() jobPost: any;
  @Output() onUpdate: EventEmitter<any> = new EventEmitter();
  @ViewChildren(MatAutocompleteTrigger) autoCompletes!: QueryList<MatAutocompleteTrigger>;

  copyPersonalForm: any;
  showEditForm: boolean = false;
  maxDate: Date = new Date();
  positionDropdown: any[] = [];
  filteredPositions: any[] = [];
  positionFilter: string = '';
  positionControl = new FormControl('');
  isExpanded = {
    address: false,
    working: false,
    position: false,
    email: false,
    portfolio: false
  };

  userData!: UserDetails;
  [x: string]: any;
  countryDropdown: any[] = [];
  stateDropdown: any[] = [];
  cityDropdown: any[] = [];
  isUploading: boolean = false;
  isOnline: boolean = navigator.onLine;

  @ViewChild('autoCompleteCountryInput', { static: false, read: MatAutocompleteTrigger }) autoCompleteCountryInput!: MatAutocompleteTrigger;
  @ViewChild('autoCompleteStateInput', { static: false, read: MatAutocompleteTrigger }) autoCompleteStateInput!: MatAutocompleteTrigger;
  @ViewChild('autoCompleteCityInput', { static: false, read: MatAutocompleteTrigger }) autoCompleteCityInput!: MatAutocompleteTrigger;

  constructor(
    private endCustomerService: EndCustomerService,
    private fb: FormBuilder,
    private masterDataService: MasterDataService,
    private localStorageService: LocalStorageService
  ) {
    window.addEventListener('scroll', this.scrollEvent, true);
  }

  scrollEvent = (event: any): void => {
    const target = event.target as HTMLElement;
    const isScrollingInsidePanel = this.autoCompletes.some(autoComplete => {
      if (autoComplete.panelOpen) {
        const panel = autoComplete.autocomplete.panel;
        return panel && panel.nativeElement.contains(target);
      }
      return false;
    });
    if (!isScrollingInsidePanel) {
      this.closePanels();
    }
  };

  toggleExpand(section: 'address' | 'working' | 'position' | 'email' | 'portfolio') {
    this.isExpanded[section] = !this.isExpanded[section];
  }

  closePanels() {
    this.autoCompletes.forEach(autoComplete => {
      if (autoComplete.panelOpen) {
        autoComplete.closePanel();
      }
    });
  }

  ngOnInit(): void {
    this.isOnline = navigator.onLine;
    const namePattern = /^[A-Za-z\s.]+$/;

    this.personalForm.get('name')?.setValidators([
      Validators.required,
      Validators.pattern(namePattern),
      Validators.maxLength(50)
    ]);
    this.personalForm.get('name')?.updateValueAndValidity();

    const orgPattern = /^[A-Za-z0-9\s&.]+$/;
    const maxLength = 150;
    this.personalForm.get('organization')?.setValidators([
      Validators.required,
      Validators.pattern(orgPattern),
      Validators.maxLength(maxLength)
    ]);
    this.personalForm.get('organization')?.updateValueAndValidity();

    this.personalForm.get('phone')?.setValidators([
      Validators.required,
      this.phoneValidator
    ]);
    this.personalForm.get('phone')?.updateValueAndValidity();

    const alphaNumericPattern = /^[A-Za-z0-9\s]+$/;
    this.personalForm.get('workingIn')?.setValidators([
      Validators.maxLength(100),
      Validators.pattern(alphaNumericPattern)
    ]);
    this.personalForm.get('workingIn')?.updateValueAndValidity();

    const addressPattern = /^[A-Za-z0-9\s.,-/#&]+$/;
    this.personalForm.get('address')?.setValidators([
      Validators.maxLength(150),
      Validators.pattern(addressPattern)
    ]);
    this.personalForm.get('address')?.updateValueAndValidity();

    if (this.showPersonalInfo) {
      this.getPositionsDropdown();
    }

    const httpsUrlPattern = (/^(https:\/\/|http:\/\/|www\.).*/);
    this.personalForm.get('url')?.setValidators([Validators.pattern(httpsUrlPattern), Validators.maxLength(150)]);
    this.personalForm.get('url')?.valueChanges.subscribe((url: string) => {
      if (url) {
        this.personalForm.get('url')?.setValue(url.toLowerCase(), { emitEvent: false });
      }
    });

    this.personalForm.get('noEmployees')?.setValidators([
      Validators.maxLength(10),
      Validators.pattern('^[0-9]+$')
    ]);
    this.personalForm.get('noEmployees')?.updateValueAndValidity();

    this.maxDate.setDate(new Date().getDate() - 1);
    this.getCountries(null).then(() => {
    }).catch(err => {
      console.error('Error loading initial countries:', err);
    });

    if (this.showPersonalInfo) {
      this.getPositionsDropdown();
    }

    this.country.valueChanges.subscribe(data => {
      if (typeof data === 'string') {
        this.getCountries({ startsWith: data }).then(() => {
        }).catch(err => {
          console.error('Error filtering countries:', err);
        });
      } else {
        this.getCountries(null).then(() => {
        }).catch(err => {
          console.error('Error resetting countries:', err);
        });
      }
    });

    this.state.valueChanges.subscribe(data => {
      const countryName = this.personalForm.get('country')?.value;
      if (countryName) {
        if (typeof data === 'string') {
          this.getStatesByCountryName({ countryName, startsWith: data }).then(() => {
          }).catch(err => {
            console.error('Error filtering states:', err);
          });
        } else {
          this.getStatesByCountryName({ countryName }).then(() => {
          }).catch(err => {
            console.error('Error resetting states:', err);
          });
        }
      } else {
        this.stateDropdown = [];
        this.cityDropdown = [];
        this.personalForm.get('state')?.reset();
        this.personalForm.get('city')?.reset();
      }
    });

    this.city.valueChanges.subscribe(data => {
      const stateName = this.personalForm.get('state')?.value;
      if (stateName) {
        if (typeof data === 'string') {
          this.getCityiesByStateName({ stateName, startsWith: data }).then(() => {
          }).catch(err => {
            console.error('Error filtering cities:', err);
          });
        } else {
          this.getCityiesByStateName({ stateName }).then(() => {
          }).catch(err => {
            console.error('Error resetting cities:', err);
          });
        }
      } else {
        this.cityDropdown = [];
        this.personalForm.get('city')?.reset();
      }
    });

    this.localStorageService.getUserDetails().subscribe(userDetails => {
      this.userData = userDetails;
    });

    this.positionControl.setValue(this.personalForm.get('position')?.value?.name || '');

    this.positionControl.valueChanges.subscribe(value => {
      if (!value) {
        this.personalForm.get('position')?.setValue(null);
      } else {
        this.filterPositions();
      }
    });
  }
  phoneValidator(control: AbstractControl) {
    const value = control.value;
    if (!value) return null;
    const pattern = /^[+\d()\-\s]+$/;
    if (!pattern.test(value)) {
      return { pattern: true };
    }
    if (value.length < 5) {
      return { minlength: true };
    }
    if (value.length > 15) {
      return { maxlength: true };
    }
    return null;
  }

  invalidZipcode: boolean = false;
  validateZipCode(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const zipCode = inputElement.value;

    if (zipCode.length < 6 || zipCode.length > 8) {
      this.invalidZipcode = true;
    } else {
      this.invalidZipcode = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {}

  editPersonalInfo() {
    this.copyPersonalForm = JSON.parse(JSON.stringify(this.personalForm.value));
    this.showEditForm = true;
  }

  getPositionsDropdown() {
    this.endCustomerService.executeGetRequestURL(MweConstants.positionUrl, null).subscribe(data => {
      if (data && data?.data && data.data.length > 0) {
        this.positionDropdown = data.data;
        const positionObj = this.positionDropdown.find((obj: any) => obj.id === this.personalForm.get('position')?.value?.id);
        if (positionObj) {
          this.personalForm.get('position')?.patchValue(positionObj);
        }
        if (!this.personalForm.get('position')?.value?.name) {
          this.filteredPositions = [...this.positionDropdown];
        }
      }
    }, error => {
      console.error('Error fetching positions:', error);
    });
  }

  filterPositions() {
    const filterValue = this.positionControl?.value?.toLowerCase() || '';
    this.filteredPositions = this.positionDropdown.filter((position: any) =>
      position?.name?.toLowerCase().includes(filterValue)
    );
  }

  onPositionSelected(event: any) {
    const selectedPosition = event.option.value;
    this.personalForm.get('position')?.patchValue(selectedPosition);
    this.positionControl.setValue(this.personalForm.get('position')?.value?.name);
  }

  onOptionActivated(event: any) {
    if (event.option) {
      this.positionControl.setValue(event.option.value.name);
    }
  }

  numberOnly(event: any): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  getCountries(params: any): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let pageNumber = 0;
      let pageSize = 200;
      let allCountries: any[] = [];
      let morePages = true;

      const queryParams = { ...params, continentNames: [''] }; // Fetch all countries, ignore continent

      this.masterDataService.getCountries(pageNumber, pageSize, queryParams.startsWith || '', queryParams.continentNames)
        .pipe(
          takeWhile(() => morePages),
          concatMap(response => {
            if (!response || !response.data || response.data.length === 0) {
              morePages = false;
              return [];
            }
            allCountries = allCountries.concat(response.data);
            pageNumber++;
            return this.masterDataService.getCountries(pageNumber, pageSize, queryParams.startsWith || '', queryParams.continentNames);
          })
        )
        .subscribe({
          next: (data) => {
            allCountries = allCountries.concat(data.data || []);
            this.countryDropdown = allCountries;
            resolve();
          },
          error: (err) => {
            console.error('Error fetching countries:', err);
            this.countryDropdown = [];
            reject(err);
          }
        });
    });
  }

  getStatesByCountryName(params: any): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!params || !params.countryName) {
        this.stateDropdown = [];
        resolve();
        return;
      }
      let pageNumber = 0;
      let pageSize = 100;
      let allStates: any[] = [];
      let morePages = true;

      this.masterDataService.getStates(pageNumber, pageSize, params.startsWith || '', [params.countryName])
        .pipe(
          expand(response => {
            if (!response || !response.data || response.data.length === 0) {
              morePages = false;
              return EMPTY;
            }
            allStates = allStates.concat(response.data);
            pageNumber++;
            return this.masterDataService.getStates(pageNumber, pageSize, params.startsWith || '', [params.countryName]);
          }),
          finalize(() => {
            this.stateDropdown = allStates;
            resolve();
          })
        )
        .subscribe({
          error: (err) => {
            console.error('Error fetching states:', err);
            this.stateDropdown = [];
            reject(err);
          }
        });
    });
  }

  getCityiesByStateName(params: any): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!params || !params.stateName) {
        this.cityDropdown = [];
        resolve();
        return;
      }
      let pageNumber = 0;
      let pageSize = 100;
      let allCities: any[] = [];
      let morePages = true;

      this.masterDataService.getCities(pageNumber, pageSize, params.startsWith || '', [params.stateName])
        .pipe(
          expand(response => {
            if (!response || !response.data || response.data.length === 0) {
              morePages = false;
              return EMPTY;
            }
            allCities = allCities.concat(response.data);
            pageNumber++;
            return this.masterDataService.getCities(pageNumber, pageSize, params.startsWith || '', [params.stateName]);
          }),
          finalize(() => {
            this.cityDropdown = allCities;
            resolve();
          })
        )
        .subscribe({
          error: (err) => {
            console.error('Error fetching cities:', err);
            this.cityDropdown = [];
            reject(err);
          }
        });
    });
  }

  getStatesDropdown() {
    this.stateDropdown = [];
    this.cityDropdown = [];
    this.setContinentAndPhoneCode();
    this.personalForm.get('state')?.reset();
    this.personalForm.get('city')?.reset();
    if (this.personalForm.get('country')?.value) {
      this.getStatesByCountryName({ countryName: this.personalForm.get('country')?.value }).then(() => {
      }).catch(err => {
        console.error('Error loading states after country selection:', err);
      });
    }
  }

  getCityDropdown() {
    this.cityDropdown = [];
    this.personalForm.get('city')?.reset();
    if (this.personalForm.get('state')?.value) {
      this.getCityiesByStateName({ stateName: this.personalForm.get('state')?.value }).then(() => {
      }).catch(err => {
        console.error('Error loading cities after state selection:', err);
      });
    }
  }

  get country() {
    return this.personalForm.get('country') as FormControl;
  }

  get state() {
    return this.personalForm.get('state') as FormControl;
  }

  get city() {
    return this.personalForm.get('city') as FormControl;
  }

  get gender() {
    return this.personalForm.get('gender') as FormControl;
  }

  ngAfterViewInit() {
    this.autoCompleteCountryInput?.panelClosingActions.subscribe(e => {
      if (e == null) {
        this.country.setValue(null);
      }
    });
    this.autoCompleteStateInput?.panelClosingActions.subscribe(e => {
      if (e == null) {
        this.state.setValue(null);
      }
    });
    this.autoCompleteCityInput?.panelClosingActions.subscribe(e => {
      if (e == null) {
        this.city.setValue(null);
      }
    });
  }

  trimAddress(event: any) {
    const addressControl = this.personalForm.get('address');
    if (addressControl && addressControl.value) {
      const trimmedValue = addressControl.value.replace(/^\s+/, '');
      addressControl.setValue(trimmedValue, { emitEvent: false });
    }
  }

  cancel() {
    this.showEditForm = false;
    this.personalForm.get('name')?.patchValue(this.copyPersonalForm?.name);
    this.personalForm.get('organization')?.patchValue(this.copyPersonalForm?.organization);
    if (this.showPersonalInfo) {
      this.personalForm.get('dob')?.patchValue(this.copyPersonalForm?.dob);
      this.personalForm.get('gender')?.patchValue(this.copyPersonalForm?.gender);
      this.personalForm.get('workingIn')?.patchValue(this.copyPersonalForm?.workingIn);
      const positionObj = this.positionDropdown.find((obj: any) => obj.id === this.copyPersonalForm?.position?.id);
      if (positionObj) {
        this.personalForm.get('position')?.patchValue(positionObj);
      }
    } else {
      this.personalForm.get('establishedIn')?.patchValue(this.copyPersonalForm?.establishedIn);
      this.personalForm.get('noEmployees')?.patchValue(this.copyPersonalForm?.noEmployees);
      this.personalForm.get('url')?.patchValue(this.copyPersonalForm?.url);
    }
    this.personalForm.get('address')?.patchValue(this.copyPersonalForm?.address);
    this.personalForm.get('phone')?.patchValue(this.copyPersonalForm?.phone);
    this.personalForm.get('country')?.patchValue(this.copyPersonalForm?.country);
    this.personalForm.get('state')?.patchValue(this.copyPersonalForm?.state);
    this.personalForm.get('city')?.patchValue(this.copyPersonalForm?.city);
    this.personalForm.get('zipCode')?.patchValue(this.copyPersonalForm?.zipCode);
    this.personalForm.get('logo')?.patchValue(this.copyPersonalForm?.logo);
  }

  update() {
    const zipCode = this.personalForm.get('zipCode')?.value;
    if (zipCode && (zipCode.length < 6 || zipCode.length > 8)) {
      this.invalidZipcode = true;
      return;
    }
    this.personalForm.get('name')?.markAsTouched();
    this.invalidZipcode = false;

    if (this.showPersonalInfo) {

    } else {
      this.personalForm.get('organization')?.markAsTouched();
    }

    this.personalForm.get('email')?.markAsTouched();
    this.personalForm.get('phone')?.markAsTouched();
    this.personalForm.get('url')?.markAsTouched();

    if (this.personalForm.get('name')?.invalid) {
      return;
    }
    if (this.personalForm.get('email')?.invalid) {
      return;
    }
    if (this.personalForm.get('phone')?.invalid) {
      return;
    }
    if (this.personalForm.get('url')?.invalid) {
      return;
    }
    if (this.personalForm.get('address')?.invalid) {
      return;
    }
    if (this.personalForm.get('workingIn')?.invalid) {
      return;
    }

    const isIndividual = this.personalForm?.get('account')?.value?.accountType?.name === 'Individual';
    const isOrganizationRequired = this.personalForm.get('organization')?.value !== null && this.personalForm.get('organization')?.value !== undefined;
    const isOrganizationInvalid = this.personalForm.get('organization')?.invalid;
     if(!isIndividual && isOrganizationRequired && isOrganizationInvalid) {
      return;
    }

    this.userData.userName = this.personalForm.get('name')?.value;
    this.localStorageService.updateUserDetails(this.userData);
    const value = this.personalForm.get('organization')?.value;
    if (value !== null && value !== undefined && value.trim().length > 0) {
      const orgranizationName = this.personalForm.get('organization')?.value.trim();
      this.personalForm.get('organization')?.patchValue(orgranizationName);
    }
    this.onUpdate.emit(0);
  }

  setContinentAndPhoneCode() {
    if (this.personalForm.get('country')?.value) {
      const countryObj = this.countryDropdown.find((obj: any) => obj.countryName === this.personalForm.get('country')?.value);
      if (countryObj) {
        this.personalForm.get('continent')?.patchValue(countryObj.continentName);
        this.personalForm.get('phoneCode')?.patchValue(countryObj.phoneCode);
      }
    }
  }

  refresh() {
    this.showEditForm = false;
    if (this.showPersonalInfo) {
      const positionObj = this.positionDropdown.find((obj: any) => obj.id === this.personalForm.get('position')?.value?.id);
      if (positionObj) {
        this.personalForm.get('position')?.patchValue(positionObj);
      }
    }
  }

  @HostListener('window:online')
  checkConnectionOnline() {
    this.isOnline = true;
  }

  @HostListener('window:offline')
  checkConnectionOffline() {
    this.isOnline = false;
  }

  uploadLogo(event: any) {
    const file = event.target.files[0];
    const allowedExtensions = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!this.isOnline) {
      Swal.fire({
        icon: 'error',
        title: 'No Internet Connection',
        text: 'Check Your Internet Connection and please try again.',
        confirmButtonText: 'Ok',
        confirmButtonColor: '#3085d6',
      });
      event.target.value = '';
      return;
    }

    if (file && !allowedExtensions.includes(file.type)) {
      Swal.fire({
        title: 'Error',
        text: 'Only JPG, JPEG and PNG files are allowed.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });
      event.target.value = '';
      return;
    }

    const maxSizeInMB = 10;
    const fileSizeInMB = event.target.files[0].size / (1024 * 1024);

    if (fileSizeInMB > maxSizeInMB) {
      event.target.value = '';
      Swal.fire({
        icon: 'error',
        title: 'File size exceeded',
        text: 'File size should not exceed 10MB'
      });
      event.target.value = '';
      return;
    }
    this.isUploading = true;
    let uploadData = new FormData();
    uploadData.append('file', event.target.files[0]);
    let randomNumber = Math.floor(Math.random() * 10) + 1;
    uploadData.append('destinationKey', randomNumber + '');

    this.endCustomerService.executePostRequestForAuth(MweConstants.uploadFile, uploadData).subscribe(data => {
      if (data && data?.data) {
        this.personalForm.get('logo')?.patchValue(data.data);
      }
      this.isUploading = false;
      event.target.value = '';
    }, error => {
      this.isUploading = false;
      event.target.value = '';
      console.error('Error uploading logo:', error);
    });
  }

  removeLogo() {
    this.personalForm.get('logo')?.patchValue(null);
  }

  urlValidator(control: AbstractControl) {
    const url = control.value;
    if (!url.startsWith('https://') || !url.startsWith('www.')) {
      return { invalidUrl: true };
    }
    return null;
  }

  openLink(url: string) {
    if (this.personalForm.get('url')?.valid) {
      const linkUrl = this.checkLink(url) ? url : `https://${url}`;
      window.open(linkUrl, '_blank');
    } else {
      this.markFormGroupTouched(this.personalForm);
    }
  }

  checkLink(url: string): boolean {
    return url?.startsWith('https://') || url?.startsWith('http://');
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  preventNumericInput(event: KeyboardEvent, geographyName: any): void {
    const charCode = event.charCode || event.keyCode;
    if (charCode >= 48 && charCode <= 57) {
      event.preventDefault();
    }

    const inputChar = event.key;
    let currentValue = '';
    if (geographyName === 'country') {
      currentValue = this.country?.value || '';
    } else if (geographyName === 'state') {
      currentValue = this.state?.value || '';
    } else if (geographyName === 'city') {
      currentValue = this.city?.value || '';
    }

    if ((currentValue.length === 0 && inputChar === ' ') || currentValue[0] === ' ' || (currentValue[0] === ' ' && inputChar === ' ')) {
      event.preventDefault();
    }
  }

  removeSpaceAtStart(event: any) {
    let inputValue = event.target.value;
    if (inputValue[0] === ' ') {
      event.target.value = inputValue.substring(1);
    }
  }
}